import React, { useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactCountryFlag } from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowStore } from "../store/sendmoneyWorkflowStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { sendMoneyService } from "../../../../services/sendmoney.service";
import { useApiWithLoading } from "../../../../hooks/useApiWithLoading";
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import { SEND_MONEY_ROUTES } from "../routes/sendMoneyRoutes";
import type { StepKey } from "../sendWorkflow/types/stepKey";
import { sendWorkflowManager } from "../sendWorkflow/builder/workflowManager";
import { requiresSecondaryId } from "../helper/transactionFieldsHelper";
import { useStepAction } from "../hooks/useStepAction";

countries.registerLocale(enLocale);

export function ReviewAndEditScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const journeyData = useJourneyStore((state) => state.journeyData);
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const setCurrentStepKey = useWorkflowStore((state) => state.setCurrentStepKey);
  const { goNext } = useWorkflowNavigation();
  const { executeWithLoading } = useApiWithLoading();

  const {
    sendAmount,
    sendCurrency,
    receiveAmount,
    receiveCurrency,
    destinationCountry,
    destinationState,
    selectedServiceOption,
  } = journeyData;

  const serviceOptionName = selectedServiceOption?.serviceOptionName || "—";

  const alpha2 = useMemo(() => {
    const a3 = (destinationCountry?.iso3Code || "").toUpperCase();
    return countries.alpha3ToAlpha2(a3) || destinationCountry?.iso2Code || "";
  }, [destinationCountry?.iso3Code, destinationCountry?.iso2Code]);

  const onEditStep = (stepKey: StepKey, route: string) => {
    setCurrentStepKey(stepKey);
    navigate(route);
  };

  const handleNext = async () => {
    const agentId = useSessionStore.getState().agentId;
    if (!agentId || !destinationCountry?.iso3Code || !selectedServiceOption) {
      return;
    }

    const option = selectedServiceOption as any;
    const response = await executeWithLoading(
      () =>
        sendMoneyService.transactionFieldsSend({
          agentPartnerId: agentId,
          destinationCountryCode: destinationCountry.iso3Code,
          serviceOptionCode: option.serviceOptionCode || "WILL_CALL",
          ...(option.serviceOptionRoutingCode && {
            serviceOptionRoutingCode: option.serviceOptionRoutingCode,
          }),
          amount: sendAmount,
          sendCurrencyCode: sendCurrency || "USD",
          receiveCurrencyCode: receiveCurrency,
        }),
      "Processing..."
    );

    updateJourneyData({ transactionFieldsSendResponse: response });
    sendWorkflowManager.updateContext({
      requiresSecondaryId: requiresSecondaryId(response.fieldInfo ?? []),
    });
    goNext();
  };

  useStepAction(handleNext);

  return (
    <View style={styles.container} testID="review-and-edit-screen">
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>

      <View style={styles.sectionRow}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{sendAmount.toFixed(2)}</Text>
            <Text style={styles.currencyText}>{sendCurrency || "USD"}</Text>
          </View>
          <Text style={[styles.inputLabel, { marginTop: 8 }]}>
            {t("REVIEW_THEY_RECEIVE")}
          </Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{receiveAmount.toFixed(2)}</Text>
            <Text style={styles.currencyText}>{receiveCurrency || "—"}</Text>
          </View>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditAmount"
          style={styles.editButton}
          onPress={() => onEditStep("AMOUNT", SEND_MONEY_ROUTES.AMOUNT)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionRow}>
        <View>
          <Text style={styles.inputLabel}>
            {t("REVIEW_DESTINATION_COUNTRY")}
          </Text>
          <View style={[styles.amountInput, { alignItems: "center" }]}>
            {Platform.OS === "web" ? (
              <View style={styles.flagOverflow}>
                {alpha2 ? (
                  <ReactCountryFlag
                    countryCode={alpha2}
                    svg
                    style={{ width: 30, height: 30, borderRadius: 50 }}
                    title={alpha2}
                  />
                ) : (
                  <Text style={{ fontSize: 16 }}>🌍</Text>
                )}
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🌍</Text>
            )}
            <Text style={styles.countryText}>
              {destinationCountry?.name || "—"}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditCountry"
          style={styles.editButton}
          onPress={() => onEditStep("COUNTRY", SEND_MONEY_ROUTES.COUNTRY)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {(destinationCountry?.iso3Code === "USA" ||
        destinationCountry?.iso3Code === "CAN") && (
        <View style={styles.sectionRow}>
          <View>
            <Text style={styles.inputLabel}>{t("DESTINATION_STATE")}</Text>
            <Text style={styles.countryText}>
              {destinationState?.name || "—"}
            </Text>
          </View>
          <TouchableOpacity
            testID="sendMoneyEditState"
            style={styles.editButton}
            onPress={() => onEditStep("STATE", SEND_MONEY_ROUTES.STATE)}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={[styles.sectionRow, { marginBottom: 24 }]}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("REVIEW_HOW_THEY_RECEIVE")}</Text>
          <Text style={[styles.pickUpText, { flexShrink: 1 }]}>
            {serviceOptionName}
          </Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditHowTheyReceive"
          style={styles.editButton}
          onPress={() =>
            onEditStep("HOW_THEY_RECEIVE", SEND_MONEY_ROUTES.HOW_THEY_RECEIVE)
          }
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={{ marginTop: 16, marginBottom: 16 }}>
        {i18n.language === "en" ? (
          <View>
            <Text style={styles.terms}>
              I agree to MoneyGram's{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Terms and Conditions
              </Text>{" "}
              and confirm I am the sole beneficial owner of the assets, and am
              acting solely on my own profile. By continuing with this
              transaction I consent to the collection, use, disclosure, and
              transfer (including cross-border transfer) of my personal
              information as described in our{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Privacy Policy.
              </Text>
            </Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              <Text style={styles.bold}>Exchange rate</Text>
              {"\n"}
              In addition to transfer fees, a currency exchange rate, also
              determined by MoneyGram, may also apply. MoneyGram may also make
              money from currency exchange. When selecting a money transfer
              operator, carefully compare both transfer fees and currency
              exchange rates. Transfer fees may vary depending on the amount
              being transferred and the delivery method. Transfer fee and
              currency exchange rate may be subject to change by MoneyGram
              without notice.
            </Text>
          </View>
        ) : (
          <View>
            <Text style={styles.terms}>{t("TERMS_AND_CONDITIONS")}</Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              {t("TERMS_EXCHANGE_POLICY")}
            </Text>
          </View>
        )}
      </View>

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    justifyContent: "flex-start",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
  },
  sectionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    marginRight: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: "Rubik",
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: "stretch",
  },
  amountInput: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  amountText: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
  },
  currencyText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 24,
    paddingLeft: 8,
  },
  editButton: {
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  editText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 16,
  },
  flagOverflow: {
    width: 16,
    height: 16,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  countryText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  pickUpText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.black,
    lineHeight: 20,
  },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: {
    fontWeight: "600",
  },
});
